import { z } from "zod";
import { ATTACHMENT_LIMITS, AttachmentSchema } from "./api";

/** M10 team chat (brief `docs/briefs/m10-team-chat.md`, draft 2.1): the schemas and the pure rules the server, the web and the tests
 *  share. The rooms and their log live in migration 0066; the room rule (who may read a room) is the database's. */

/** The ticket discussion round (2026-11-02, finding 11): the rooms read lists the ticket rooms with a message in the last `recentDays` and every
 *  followed one with unread, at most `max` (followed unread first, then the newest message); the server's cap and the rail's "Show all" share it */
export const CHAT_TICKET_ROOMS = { recentDays: 30, max: 30 } as const;
/** the discussion's read beside the ticket answers every message up to this many (a sanity cap: the timeline shows the whole discussion) */
export const CHAT_DISCUSSION_MAX = 500;
/** One name for a ticket's discussion wherever it shows (the rail, the room's head, the mention mail, the browser notification):
 *  "#123 · Subject", "(no subject)" when the ticket has none */
export function ticketRoomLabel(number: number, subject: string | null | undefined): string {
  return `#${number} · ${subject?.trim() || "(no subject)"}`;
}

/** a message may hold as much as a ticket note (choice 10); the room folds anything past CHAT_FOLD_CHARS behind "Show more" */
export const CHAT_MESSAGE_MAX = 50_000;
export const CHAT_FOLD_CHARS = 4_000;
/** one page of a room's history (round one) */
export const CHAT_PAGE_SIZE = 50;
/** the people one message may mention (choice 10) */
export const CHAT_MENTIONS_MAX = 20;
/** an unread count above this shows as "99+" (choice 4) */
export const CHAT_UNREAD_CAP = 99;
/** Team chat's limits (choice 10): messages (edits count) a minute and a day per person and workspace, channels a person opens a day,
 *  open (not archived) channels a workspace holds, people on a private channel's list. The per-person ones follow the API's rate-limit
 *  switch (as the connector door's per-member limits); the two caps always hold. */
export const CHAT_LIMITS = { perMinute: 30, perDay: 2000, channelsPerDay: 20, openChannels: 200, roomMembers: 200 } as const;
/** a channel's topic, at most this long */
export const CHAT_TOPIC_MAX = 250;
/** every event of the log is one of these (choice 2): an edit is its own event pointing at the original with relation "replace"; a system
 *  line (migration 0070) is the room's own note "archived by <admin>", written when a manager outside archives a private channel: never
 *  unread, never mailed, never found by the search */
export const CHAT_EVENT_TYPES = ["message", "edit", "system"] as const;
export type ChatEventType = (typeof CHAT_EVENT_TYPES)[number];
/** the ticket discussion round (2026-11-02): a third kind, `ticket`, the team's own room about one ticket (its Discussion tab) */
export const CHAT_ROOM_KINDS = ["channel", "dm", "ticket"] as const;
export type ChatRoomKind = (typeof CHAT_ROOM_KINDS)[number];
/** the live notes' kinds (packages/db's EventKind names the same four): ids and flags only, never a ticket or the text */
export const CHAT_NOTE_KINDS = ["chat.message", "chat.updated", "chat.room", "chat.read"] as const;

/** the stored document of an event (chat_events.content): a version, the text, the ticket numbers the server found in it */
export const ChatContentSchema = z.object({ v: z.literal(1), text: z.string().max(CHAT_MESSAGE_MAX), tickets: z.array(z.number().int().positive()) }).strict();
export type ChatContent = z.infer<typeof ChatContentSchema>;

const ChatText = z.string().min(1).max(CHAT_MESSAGE_MAX).refine((t) => t.trim().length > 0, "A message needs some text.");
const ChatMentions = z.array(z.string().min(1)).max(CHAT_MENTIONS_MAX).optional();
/** POST …/chat/rooms/:id/events: the browser's retry id, the text, the colleagues it mentions and (round three) the files uploaded into this
 *  room for it, at most 20 (`attachmentIds`, the ticket composer's name); a message with files may have no text. The sender, the number
 *  and the tickets are the server's. */
export const ChatSendSchema = z.object({
  clientTxId: z.string().min(8).max(100),
  text: z.string().max(CHAT_MESSAGE_MAX),
  mentions: ChatMentions,
  attachmentIds: z.array(z.string().min(1)).max(ATTACHMENT_LIMITS.files).optional(),
}).strict().refine((b) => b.text.trim().length > 0 || (b.attachmentIds?.length ?? 0) > 0, { message: "A message needs some text.", path: ["text"] });
export type ChatSend = z.infer<typeof ChatSendSchema>;

/** PUT …/chat/rooms/:id/read: the number the reader has seen up to (moved forward only, clamped to the newest) */
export const ChatReadSchema = z.object({ seq: z.number().int().min(0) }).strict();

/** GET …/chat/rooms/:id/events?before=|after=: one cursor at most; none is the newest page */
export const ChatEventsQuerySchema = z.object({ before: z.coerce.number().int().min(1).optional(), after: z.coerce.number().int().min(0).optional() }).strict()
  .refine((q) => q.before === undefined || q.after === undefined, "Give before or after, not both.");

/** "Design Reviews " → "design-reviews" (the prototype's rule, written once for the server and the New channel dialog): trimmed, lower
 *  case, every run of anything but a–z and 0–9 one dash, no dash at either end, at most 40 characters. Empty: the name has no letter or digit. */
export function channelSlug(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40).replace(/-+$/, "");
}

/** POST …/chat/rooms: a new channel; its slug is the name by `channelSlug` (round two) */
export const ChatRoomCreateSchema = z.object({ name: z.string().min(1).max(80), topic: z.string().max(CHAT_TOPIC_MAX).optional(), private: z.boolean().optional() }).strict();
/** PATCH …/chat/rooms/:id: rename (the slug follows), the topic, private or public, archived or restored; at least one of them */
export const ChatRoomPatchSchema = z.object({ name: z.string().min(1).max(80).optional(), topic: z.string().max(CHAT_TOPIC_MAX).nullable().optional(), private: z.boolean().optional(), archived: z.boolean().optional() }).strict()
  .refine((p) => Object.keys(p).length > 0, "Nothing to change.");
/** one management screen (2026-10-12): the open channels the caller sees, every one once, in their new order (owners and administrators) */
export const ChatRoomOrderSchema = z.object({ ids: z.array(z.string().min(1)).min(1).max(500) }).strict();
export type ChatRoomPatch = z.infer<typeof ChatRoomPatchSchema>;
/** POST …/chat/dms: the colleague; the DM is found or made */
export const ChatDmSchema = z.object({ userId: z.string().min(1).max(200) }).strict();
/** PATCH …/chat/rooms/:id/events/:eventId: the whole new text of the sender's own message (a replacement event) */
export const ChatEditSchema = z.object({ text: ChatText, mentions: ChatMentions }).strict();
/** GET …/chat/search?q=&room=: the words, and the one room they are looked for in */
export const ChatSearchQuerySchema = z.object({ q: z.string().max(200).default(""), room: z.string().min(1).max(200) }).strict();

/** one event as a room's read answers it: a ticket's subject only for a reader who may see its project (else null) */
export const ChatEventSchema = z.object({
  id: z.string(),
  seq: z.number().int(),
  type: z.enum(CHAT_EVENT_TYPES),
  senderId: z.string().nullable(),
  text: z.string(),
  tickets: z.array(z.object({ number: z.number().int().positive(), subject: z.string().nullable() })),
  mentions: z.array(z.string()),
  relatesToId: z.string().nullable(),
  relation: z.literal("replace").nullable(),
  redactedAt: z.string().nullable(),
  createdAt: z.string(),
  /** round two: when the newest replacement was written (the text above is already its text); null when never edited or deleted */
  editedAt: z.string().nullable().optional(),
  /** round three: the files sent with the message (the ticket upload's shape; addresses under /api/v1/files/chat/); none once deleted */
  attachments: z.array(AttachmentSchema).optional(),
  /** ticket discussion round two: the mark of a line the member's own AI wrote through the connector (which app, which model), read from the
   *  original line so an edit keeps it; written only by server code (people cannot send it); absent or null on a person's line */
  via: z.object({ app: z.string().nullable(), model: z.string().nullable() }).nullable().optional(),
});
export type ChatEvent = z.infer<typeof ChatEventSchema>;

/** one room as the rooms read lists it for the reader: a DM names the other person (`dmUserId`); `unread` and `mentions` follow the
 *  unread rule (choice 4); `lastSeq` is the newest message's number, `readSeq` the reader's marker */
export const ChatRoomSchema = z.object({
  id: z.string(),
  kind: z.enum(CHAT_ROOM_KINDS),
  slug: z.string().nullable(),
  name: z.string().nullable(),
  topic: z.string().nullable(),
  private: z.boolean(),
  dmUserId: z.string().nullable(),
  /** who is in the room: a DM's two people, a private channel's list; null for a public channel (every active member) */
  memberIds: z.array(z.string()).nullable(),
  unread: z.number().int().min(0),
  mentions: z.number().int().min(0),
  lastSeq: z.number().int().min(0),
  readSeq: z.number().int().min(0),
  lastEventAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  /** round two: who made the channel (null for #general and the workspace's own); its maker manages it with the team's managers */
  createdBy: z.string().nullable().optional(),
  /** The ticket discussion round (2026-11-02): a ticket room's ticket (for the rail's "#123 · Subject", the head's link and pill), whether the
   *  reader follows it and who does. Absent on channels and DMs, which answer as before. For a ticket room `memberIds` is the active members
   *  who may see the ticket (whom the @ list offers), and `unread`/`mentions` count for followers only. */
  ticketId: z.string().nullable().optional(),
  ticketNumber: z.number().int().positive().nullable().optional(),
  ticketSubject: z.string().nullable().optional(),
  ticketStatus: z.string().nullable().optional(),
  projectId: z.string().nullable().optional(),
  following: z.boolean().optional(),
  followerIds: z.array(z.string()).optional(),
});
export type ChatRoom = z.infer<typeof ChatRoomSchema>;
/** A private channel the reader is not in, as a holder of `team` sees it in Manage channels (DECISIONS "2026-10-07 — Private channels managed
 *  from outside"): enough to manage it, never what is said in it or who is in it beyond a count. `GET …/chat/rooms/private` lists them; an
 *  outside rename, archive, restore or "Add people" answers the room in this shape. */
export const ChatOutsideRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  memberCount: z.number().int().min(0),
  createdBy: z.string().nullable(),
  lastEventAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
}).strict();
export type ChatOutsideRoom = z.infer<typeof ChatOutsideRoomSchema>;
export const ChatOutsideRoomsSchema = z.object({ rooms: z.array(ChatOutsideRoomSchema) });
export const ChatRoomsSchema = z.object({ rooms: z.array(ChatRoomSchema) });
export const ChatEventsSchema = z.object({ events: z.array(ChatEventSchema) });
/** the search in a room: its messages whose newest text holds every word, newest first, each under its own id */
export const ChatSearchSchema = z.object({ hits: z.array(ChatEventSchema) });
/** GET …/chat/rooms/by-ticket/:number/events (the ticket discussion round): the ticket's room as the rooms read lists it (null before the first
 *  message) and every message of it in order; POST to the same address (the chat send's body) answers the room and the new message */
/** ticket discussion round two (finding 13): `formerAuthors`, the names of senders who are no longer members of the workspace (removed), so
 *  their lines keep their name; a deleted account is absent and reads "Former member"; left out when there are none */
export const ChatDiscussionSchema = z.object({ room: ChatRoomSchema.nullable(), events: z.array(ChatEventSchema), formerAuthors: z.array(z.object({ id: z.string(), name: z.string() })).optional() });
export type ChatDiscussion = z.infer<typeof ChatDiscussionSchema>;

/** The ticket numbers a message names, in order of first appearance, each once (choice 3, review S8): `#` and one to seven digits standing
 *  alone (at the start, after white space or an opening bracket; ending at white space, punctuation or the end). Never inside a word or an
 *  address (`issue#12`, `…/page#3`), never `#0`, never digits running into letters (`#12abc`). */
export function ticketRefsIn(text: string): number[] {
  const out: number[] = [];
  for (const m of text.matchAll(/(?:^|(?<=[\s([{]))#([1-9]\d{0,6})(?![\p{L}\p{N}_])/gu)) {
    const n = Number(m[1]);
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/** The read marker after a reader asks for `requested` (choice 4): it only moves forward and never past the room's newest message. */
export function nextReadMarker(current: number, requested: number, newest: number): number {
  return Math.max(current, Math.min(requested, newest));
}

/** What a count badge shows: nothing at zero, the number up to 99, then "99+". */
export function unreadLabel(n: number): string | null {
  return n <= 0 ? null : n > CHAT_UNREAD_CAP ? `${CHAT_UNREAD_CAP}+` : String(n);
}

/** A DM's pair as stored: lower id first (the database checks it), so two people have one room. */
export function dmPair(x: string, y: string): [string, string] {
  return x < y ? [x, y] : [y, x];
}

/** What waits for the reader in team chat (round three, choice 4; the chat dot round, 2026-11-01): `badge`, the coloured dot on the rail and
 *  the phone's tab bar, counts the mentions in channels and (the ticket discussion round) ticket rooms plus the unread messages in DMs (a DM is personal through and through, so its
 *  mentions are already in its unread); `unread`, the quiet grey dot, counts every unread message in every room; Home's "Chat mentions" row
 *  counts mentions only, in any room, and names the rooms they wait in (most mentions first; a ticket room by its `ticketNumber`). The notification switches decide mails and
 *  browser pushes only, never these counts (the 2026-10-17 rule overturned on 2026-11-01). */
export const ChatCountsSchema = z.object({
  badge: z.number().int().min(0),
  unread: z.number().int().min(0),
  mentions: z.number().int().min(0),
  mentionRooms: z.array(z.object({ id: z.string(), kind: z.enum(CHAT_ROOM_KINDS), slug: z.string().nullable(), dmUserId: z.string().nullable(), mentions: z.number().int().min(1), ticketNumber: z.number().int().positive().nullable().optional() })),
});
export type ChatCounts = z.infer<typeof ChatCountsSchema>;
/** a room as the counts read it (the rooms read's `ChatRoom`; `archivedAt` absent reads as an open room) */
type CountedRoom = Pick<ChatRoom, "id" | "kind" | "slug" | "dmUserId" | "unread" | "mentions"> & Partial<Pick<ChatRoom, "archivedAt" | "ticketNumber">>;
const sum = (rooms: CountedRoom[], of: (r: CountedRoom) => number) => rooms.reduce((n, r) => n + of(r), 0);
/** the counts from the rooms' own unread marks; an archived channel counts nowhere (2026-11-01: the chat column hides its number and nobody
 *  writes there any more, so a mark left in it could never be cleared), while the rooms keep their marks (the chat column's own state) */
export function chatCountsOf(rooms: CountedRoom[]): ChatCounts {
  const live = rooms.filter((r) => !(r.kind === "channel" && r.archivedAt));
  const roomMentions = sum(live.filter((r) => r.kind !== "dm"), (r) => r.mentions); // channels and ticket rooms (a DM's mentions are in its unread)
  const unreadDms = sum(live.filter((r) => r.kind === "dm"), (r) => r.unread);
  const mentioned = live.filter((r) => r.mentions > 0).sort((a, b) => b.mentions - a.mentions);
  const mentionRooms = mentioned.map((r) => ({ id: r.id, kind: r.kind, slug: r.slug, dmUserId: r.dmUserId, mentions: r.mentions, ticketNumber: r.ticketNumber ?? null }));
  return { badge: roomMentions + unreadDms, unread: sum(live, (r) => r.unread), mentions: sum(mentioned, (r) => r.mentions), mentionRooms };
}
